import dayjs from 'dayjs';

export type MfaCodeKind = 'totp' | 'recovery';

export const TOTP_CODE_LENGTH = 6;
/** Characters in a recovery code without its dash: XXXXX-XXXXX. */
export const RECOVERY_CODE_LENGTH = 10;
/** A recovery code as typed, with its dash. */
export const RECOVERY_CODE_INPUT_LENGTH = RECOVERY_CODE_LENGTH + 1;

/** Keeps only digits, at most TOTP_CODE_LENGTH of them. */
export function normalizeTotpCode(input: string): string {
  return input.replace(/\D/g, '').slice(0, TOTP_CODE_LENGTH);
}

/** Upper-cases a recovery code and limits it to XXXXX-XXXXX characters. */
export function normalizeRecoveryCodeInput(input: string): string {
  return input
    .toUpperCase()
    .replace(/[^0-9A-Z-]/g, '')
    .slice(0, RECOVERY_CODE_INPUT_LENGTH);
}

/** True when `code` has all its characters, so it can be sent. */
export function isCompleteCode(kind: MfaCodeKind, code: string): boolean {
  return kind === 'totp'
    ? code.length === TOTP_CODE_LENGTH
    : code.replace(/-/g, '').length === RECOVERY_CODE_LENGTH;
}

/** The base32 secret from an otpauth:// URL, or '' if it has none. */
export function totpSecretFromUrl(otpauthUrl: string): string {
  try {
    return new URL(otpauthUrl).searchParams.get('secret') ?? '';
  } catch {
    return '';
  }
}

/** Splits a base32 secret into groups of 4 so a person can type it. */
export function formatTotpSecret(secret: string): string {
  return secret.match(/.{1,4}/g)?.join(' ') ?? '';
}

export const RECOVERY_CODES_FILENAME = 'nomadmd-recovery-codes.txt';

/** The text that Copy and Download give for a set of recovery codes. */
export function recoveryCodesText(
  codes: string[],
  email?: string | null,
  createdAt: Date = new Date(),
): string {
  return [
    `NomadMD recovery codes${email ? ` for ${email}` : ''}`,
    `Created ${dayjs(createdAt).format('MMM D, YYYY h:mm A')}`,
    '',
    'Each code signs you in once if you cannot use your passkey or authenticator app.',
    'Keep these codes somewhere safe, such as a password manager.',
    '',
    ...codes,
    '',
  ].join('\n');
}

export function downloadTextFile(filename: string, text: string): void {
  const url = URL.createObjectURL(
    new Blob([text], { type: 'text/plain;charset=utf-8' }),
  );
  const link = document.createElement('a');

  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
